import { createContext, useCallback, useContext, useEffect, useId, useRef, useState, type ButtonHTMLAttributes, type HTMLAttributes, type ReactNode } from 'react';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { bloquearDataInvalida } from './validacao-local';
import { anunciarOcultos } from './sensivel';
import { ChevronLeft, ChevronRight, Info, LoaderCircle } from 'lucide-react';

type Variante = 'primario' | 'secundario' | 'fantasma' | 'perigo';

const VARIANTES: Record<Variante, string> = {
  primario: 'bg-acento text-acento-tinta hover:bg-acento-hover border-transparent',
  secundario: 'bg-sup-2 text-tinta-1 hover:bg-sup-3 border-borda',
  fantasma: 'bg-transparent text-tinta-2 hover:bg-sup-2 hover:text-tinta-1 border-transparent',
  perigo: 'bg-transparent text-perigo hover:bg-sup-2 border-borda',
};

/**
 * Classes do `Botao` (variante e tamanho). Review r16 visual B3: um link que faz papel de botão (baixar um arquivo) usa estas
 * classes em vez de um estilo próprio, e fica igual ao botão de mesma variante. Lote 19 (design system da Malgo): no celular
 * (abaixo de 768px) todo botão tem alvo de toque de 44x44px (BrandBook); a partir dali, as alturas de antes (40 e 32px).
 */
export function classesBotao(variante: Variante = 'secundario', tamanho: 'sm' | 'md' = 'md') {
  return `inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 ${
    tamanho === 'sm' ? 'h-11 min-w-11 px-3 text-sm md:h-8 md:min-w-0' : 'h-11 min-w-11 px-4 text-sm md:h-10 md:min-w-0'
  } ${VARIANTES[variante]}`;
}

export function Botao({
  variante = 'secundario',
  tamanho = 'md',
  carregando,
  ocupado,
  icone,
  children,
  className = '',
  onClick,
  ...resto
}: ButtonHTMLAttributes<HTMLButtonElement> & { variante?: Variante; tamanho?: 'sm' | 'md'; carregando?: boolean; ocupado?: boolean; icone?: ReactNode }) {
  // Em carregamento o botão fica `aria-disabled` (não `disabled`): mantém o foco e o lugar no Tab, e o clique é bloqueado aqui.
  const bloqueado = !!(carregando || ocupado);
  return (
    <button
      type="button"
      // Botão de ação não sai no papel (r8 visual M2); quem precisar imprimir passa data-sem-impressao={undefined}.
      data-sem-impressao=""
      // Fase 2 (W1): a ação rápida do Início acha o primário da aba por aqui (`SlotAcaoPrimaria`).
      data-variante={variante}
      {...resto}
      aria-disabled={bloqueado || undefined}
      onClick={(e) => {
        if (bloqueado) return e.preventDefault();
        // Data inválida no formulário ou na camada (modal, drawer) barra a ação primária (r5 robustez M4).
        if (variante === 'primario') {
          const raiz = e.currentTarget.closest('form, [role="dialog"]');
          if (raiz && bloquearDataInvalida(raiz)) return e.preventDefault();
        }
        onClick?.(e);
      }}
      className={`${classesBotao(variante, tamanho)} ${className}`}
    >
      {carregando ? <LoaderCircle className="size-4 animate-spin" aria-hidden /> : icone}
      {children}
    </button>
  );
}

/**
 * V4 (20/10, pedido do Gustavo): dentro deste provedor, todo `Cartao` com título nasce recolhido e abre pelo título (a ficha
 * de Pessoas e empresas). Os cartões que vêm de outros módulos (Cobrança, Portal, Onboarding) entram sem mudar nada neles.
 */
const CartoesRecolhidos = createContext(false);
export function ProvedorCartoesRecolhidos({ children }: { children: ReactNode }) {
  return <CartoesRecolhidos.Provider value={true}>{children}</CartoesRecolhidos.Provider>;
}

function CartaoRecolhivel({ titulo, acao, children, className, semPadding }: { titulo: ReactNode; acao?: ReactNode; children: ReactNode; className: string; semPadding?: boolean }) {
  const [aberto, setAberto] = useState(false);
  const id = useId();
  return (
    <section data-cartao-recolhivel="" className={`min-w-0 rounded-xl border border-borda bg-sup-1 shadow-cartao ${className}`}>
      <header className={`flex items-center justify-between gap-3 px-4 py-1 ${aberto ? 'border-b border-borda' : ''}`}>
        <h2 className="min-w-0 flex-1 text-base font-semibold text-tinta-1">
          <button
            type="button"
            aria-expanded={aberto}
            aria-controls={id}
            onClick={() => setAberto((a) => !a)}
            className="flex min-h-11 w-full min-w-0 items-center gap-2 rounded text-left break-words md:min-h-10"
          >
            <ChevronRight className={`size-4 shrink-0 text-tinta-3 transition-transform motion-reduce:transition-none ${aberto ? 'rotate-90' : ''}`} aria-hidden />
            <span className="min-w-0">{titulo}</span>
          </button>
        </h2>
        {acao}
      </header>
      <div id={id} hidden={!aberto} className={semPadding ? '' : 'p-4'}>
        {children}
      </div>
    </section>
  );
}

export function Cartao({ titulo, acao, children, className = '', semPadding }: { titulo?: ReactNode; acao?: ReactNode; children: ReactNode; className?: string; semPadding?: boolean }) {
  const recolhido = useContext(CartoesRecolhidos);
  if (recolhido && titulo)
    return (
      <CartaoRecolhivel titulo={titulo} acao={acao} className={className} semPadding={semPadding}>
        {children}
      </CartaoRecolhivel>
    );
  return (
    <section className={`min-w-0 rounded-xl border border-borda bg-sup-1 shadow-cartao ${className}`}>
      {(titulo || acao) && (
        <header className="flex items-center justify-between gap-3 border-b border-borda px-4 py-3">
          <h2 className="min-w-0 text-base font-semibold break-words text-tinta-1">{titulo}</h2>
          {acao}
        </header>
      )}
      <div className={semPadding ? '' : 'p-4'}>{children}</div>
    </section>
  );
}

export type CorSelo = 'neutro' | 'acento' | 'roxo' | 'azul' | 'ciano' | 'laranja' | 'dourado' | 'verde' | 'perigo';
// Lote 19 design 2: como o selo do ERP antigo, fundo tingido da própria cor (8%) além do contorno; o de atenção (dourado) usa o
// amarelo de realce do BrandBook em tinta clara. Texto em AA sobre o tingido (tests/contraste.test.ts).
const CORES: Record<CorSelo, string> = {
  neutro: 'text-tinta-2 border-borda bg-sup-2',
  acento: 'text-acento-forte border-acento-forte bg-acento-suave',
  roxo: 'text-roxo border-roxo bg-roxo/8',
  azul: 'text-azul border-azul bg-azul/8',
  ciano: 'text-ciano border-ciano bg-ciano/8',
  laranja: 'text-laranja border-laranja bg-laranja/8',
  dourado: 'text-dourado border-dourado bg-realce-suave',
  verde: 'text-verde border-verde bg-verde/8',
  perigo: 'text-perigo border-perigo bg-perigo/8',
};

/**
 * `quebra`: o texto pode passar para a linha de baixo (rótulo longo em tela estreita); o padrão é uma linha só. Review r21
 * visual (M1): `whitespace-normal` explícito, porque dentro de um pai com `truncate` (cartão da Tabela no celular) o `nowrap`
 * herdado segurava o rótulo numa linha e o selo cortava.
 */
export function Selo({ cor = 'neutro', children, quebra }: { cor?: CorSelo; children: ReactNode; quebra?: boolean }) {
  // Lote 19: badge com o raio sm (4px) do BrandBook, não pílula.
  return <span className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-medium ${quebra ? 'max-w-full min-w-0 break-words whitespace-normal' : 'w-fit whitespace-nowrap'} ${CORES[cor]}`}>{children}</span>;
}

/**
 * Indicador que ainda espera o valor (`...`) e já não há nenhuma consulta em andamento por mais de um instante
 * é um indicador que falhou: a API caiu ou recusou. Mostra o estado de erro em vez de ficar carregando para sempre.
 */
const INTERVALO_RETENTATIVA_MS = 8000;

function useIndicadorFalhou(aguardando: boolean) {
  const buscando = useIsFetching();
  const qc = useQueryClient();
  const [falhou, setFalhou] = useState(false);
  // Enquanto o indicador não tem valor, as consultas que falharam são refeitas sozinhas (a API volta sem o usuário recarregar a página).
  const tentarDeNovo = useCallback(() => void qc.refetchQueries({ predicate: (q) => q.state.status === 'error' || (q.state.fetchStatus === 'idle' && q.state.data === undefined) }), [qc]);
  useEffect(() => {
    if (!aguardando) return;
    const id = setInterval(tentarDeNovo, INTERVALO_RETENTATIVA_MS);
    return () => clearInterval(id);
  }, [aguardando, tentarDeNovo]);
  useEffect(() => {
    if (!aguardando || buscando > 0) {
      setFalhou(false);
      return;
    }
    const id = setTimeout(() => setFalhou(true), 800);
    return () => clearTimeout(id);
  }, [aguardando, buscando]);
  return { falhou, tentarDeNovo };
}

const FUNDOS_INDICADOR = { ciano: 'border-ciano/30 bg-ciano/8', verde: 'border-verde/30 bg-verde/8', perigo: 'border-perigo/30 bg-perigo/8' } as const;

/**
 * Indicador do núcleo. C1 (A receber e A pagar): com `aoClicar`, o card vira botão de recorte da lista (`aria-pressed`); o
 * ativo usa o estado "pressionado" do `Segmentado` (`border-acento-forte bg-acento-suave`), não um visual novo.
 */
export function Indicador({
  rotulo,
  valor,
  detalhe,
  cor = 'tinta-1',
  fundo,
  ajuda,
  ativo,
  aoClicar,
  ...dados
}: {
  rotulo: string;
  valor: ReactNode;
  detalhe?: ReactNode;
  cor?: 'tinta-1' | 'ciano' | 'laranja' | 'verde' | 'perigo' | 'dourado';
  /** Ajustes do CRM de 27/10 (C11): fundo tingido da cor (o mesmo tingido do `Selo`), como os cards de valor da empresa no RD. Aditivo. */
  fundo?: 'ciano' | 'verde' | 'perigo';
  /** Ajustes do CRM de 27/10 (C11): explicação do número num ícone "i" ao lado do rótulo (title e texto para leitor de tela). Aditivo. */
  ajuda?: string;
  ativo?: boolean;
  aoClicar?: () => void;
  [dado: `data-${string}`]: string;
}) {
  const { t } = useTranslation();
  const { falhou, tentarDeNovo } = useIndicadorFalhou(valor === '...');
  if (falhou) {
    return (
      <div className="@container min-w-0">
        <div role="alert" className="h-full rounded-xl border border-perigo bg-sup-1 p-4 shadow-cartao">
          <p className="kicker truncate" title={rotulo}>{rotulo}</p>
          <p className="mt-2 text-sm font-semibold text-perigo">{t('estados.indicadorErro')}</p>
          <p className="mt-1 text-xs break-words text-tinta-3">{t('estados.indicadorErroDetalhe')}</p>
          <Botao className="mt-2" tamanho="sm" variante="secundario" onClick={tentarDeNovo}>
            {t('estados.tentarDeNovo')}
          </Botao>
        </div>
      </div>
    );
  }
  const cores = { 'tinta-1': 'text-tinta-1', ciano: 'text-ciano', laranja: 'text-laranja', verde: 'text-verde', perigo: 'text-perigo', dourado: 'text-dourado' };
  const conteudo = (
    <>
        {ajuda ? (
          <p className="kicker flex items-center gap-1" title={rotulo}>
            <span className="truncate" title={rotulo}>{rotulo}</span>
            <span className="ml-auto shrink-0 text-tinta-3" title={ajuda}>
              <Info className="size-3.5" aria-hidden />
              <span className="sr-only">{ajuda}</span>
            </span>
          </p>
        ) : (
          <p className="kicker truncate" title={rotulo}>{rotulo}</p>
        )}
        {/* Fase 2 (W31, 0.8): nenhum valor estoura o card (o SHA de 40 caracteres da Versão no ar saía pela borda): uma linha só,
            cortada com reticências e o texto inteiro no title quando é texto. */}
        <p
          className={`numero mt-2 overflow-hidden text-lg font-bold text-ellipsis whitespace-nowrap @3xs:text-xl @xs:text-2xl ${cores[cor]}`}
          title={typeof valor === 'string' || typeof valor === 'number' ? String(valor) : undefined}
        >
          {anunciarOcultos(valor)}
        </p>
        {detalhe && <p className="mt-1 text-xs break-words text-tinta-3">{detalhe}</p>}
    </>
  );
  return (
    <div className="@container min-w-0">
      {aoClicar ? (
        <button
          type="button"
          aria-pressed={!!ativo}
          onClick={aoClicar}
          {...dados}
          className={`block h-full w-full rounded-xl border p-4 text-left shadow-cartao ${ativo ? 'border-acento-forte bg-acento-suave' : 'border-borda bg-sup-1 hover:bg-sup-2'}`}
        >
          {conteudo}
        </button>
      ) : (
        <div className={`h-full rounded-xl border p-4 shadow-cartao ${fundo ? FUNDOS_INDICADOR[fundo] : 'border-borda bg-sup-1'}`} {...dados}>
          {conteudo}
        </div>
      )}
    </div>
  );
}

/**
 * Grupo de botões alternados (o "Agrupar por" do Fluxo de caixa). C1: promovido dos relatórios para o núcleo, para o
 * "Vencimento | Competência" de A receber e A pagar, o "Todas | Entradas | Saídas" das Movimentações e os modos do Plano de contas.
 */
export function Segmentado<T extends string>({ rotulo, valor, opcoes, aoMudar, ...dados }: { rotulo: string; valor: T; opcoes: { valor: T; rotulo: string }[]; aoMudar: (v: T) => void; [dado: `data-${string}`]: string }) {
  return (
    <div role="group" aria-label={rotulo} className="inline-flex max-w-full overflow-hidden rounded-lg border border-borda" {...dados}>
      {opcoes.map((o) => (
        <button
          key={o.valor}
          type="button"
          aria-pressed={valor === o.valor}
          onClick={() => aoMudar(o.valor)}
          className={`h-11 border-b-2 px-3 text-sm font-medium whitespace-nowrap md:h-10 ${valor === o.valor ? 'border-acento-forte bg-acento-suave text-acento-forte' : 'border-transparent text-tinta-2 hover:bg-sup-2'}`}
        >
          {o.rotulo}
        </button>
      ))}
    </div>
  );
}

/**
 * Área com rolagem horizontal que avisa quando há mais conteúdo fora da vista: botões nas bordas
 * (sem degradê) que aparecem só quando existe para onde rolar e rolam a área ao clicar.
 */
export function RolagemHorizontal({
  children,
  className = '',
  classeInterna = '',
  classeBotoes = 'top-16',
  propsInterna,
}: {
  children: ReactNode;
  className?: string;
  classeInterna?: string;
  classeBotoes?: string;
  /** Review r17 visual M1: atributos da área que rola (nome, papel e foco da região de índices do celular). */
  propsInterna?: HTMLAttributes<HTMLDivElement>;
}) {
  const { t } = useTranslation();
  const area = useRef<HTMLDivElement>(null);
  const [lados, setLados] = useState({ esquerda: false, direita: false });
  const medir = useCallback(() => {
    const el = area.current;
    if (!el) return;
    const esquerda = el.scrollLeft > 4;
    const direita = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
    setLados((l) => (l.esquerda === esquerda && l.direita === direita ? l : { esquerda, direita }));
  }, []);
  useEffect(() => {
    const el = area.current;
    if (!el) return;
    medir();
    const obs = new ResizeObserver(medir);
    obs.observe(el);
    if (el.firstElementChild) obs.observe(el.firstElementChild);
    return () => obs.disconnect();
  }, [medir]);
  const rolar = (sentido: 1 | -1) => area.current?.scrollBy({ left: sentido * Math.max(240, (area.current?.clientWidth ?? 0) * 0.7), behavior: 'smooth' });
  const botao = `absolute ${classeBotoes} z-10 flex size-11 items-center justify-center rounded-full border border-tinta-3 bg-sup-3 text-tinta-1 shadow-pop hover:bg-sup-2 md:size-9`;
  return (
    <div className={`relative min-w-0 ${className}`}>
      <div {...propsInterna} ref={area} onScroll={medir} data-rolagem-horizontal className={`overflow-x-auto ${classeInterna}`}>
        {children}
      </div>
      {/* r9 visual B6: fora da ordem do Tab. O botão some quando a rolagem suave chega à borda; com o foco nele, o Tab seguinte
          caía no body. O conteúdo (abas, cartões, células) rola sozinho ao receber o foco, então o teclado não perde nada. */}
      {lados.esquerda && (
        <button type="button" tabIndex={-1} aria-label={t('comum.rolarEsquerda')} onClick={() => rolar(-1)} className={`${botao} left-1`}>
          <ChevronLeft className="size-4" aria-hidden />
        </button>
      )}
      {lados.direita && (
        <button type="button" tabIndex={-1} aria-label={t('comum.rolarDireita')} onClick={() => rolar(1)} className={`${botao} right-1`}>
          <ChevronRight className="size-4" aria-hidden />
        </button>
      )}
    </div>
  );
}
